import React, { useState, useEffect, useCallback } from 'react';
import { customerAnalysis } from '../services/api';
import ListStatus from '../components/ListStatus';

/**
 * Customer Analysis report. One customer's whole history laid out the same way every
 * time: summary tiles, orders and spend by year, what they buy, problems on file and
 * every order. Built Oct 16, 2026 from the one-off "customer synopsis" page Ask made
 * for a VIP win-back, so Matt and CS can pull the same view for any customer with an
 * issue. Opened from Customers > Customer Analysis, the links on Order Details and
 * Customer Search, and Ask's customer_analysis tool. ?q= is an email, a customer
 * number or an order number. The numbers live in netlify/functions/lib/customer-analysis.js.
 */

const NAVY = '#003b5c';
const ORANGE = '#dc582a';
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const money = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const money0 = (n) => `$${Math.round(Number(n || 0)).toLocaleString('en-US')}`;
function fmtDate(iso) {
  if (!iso) return '-';
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  if (!y) return '-';
  return `${MONTHS[m - 1]} ${d}, ${y}`;
}
const fmtMonthYear = (iso) => (iso ? `${MONTHS[Number(iso.slice(5, 7)) - 1]} ${iso.slice(0, 4)}` : '-');
function yearsSince(iso) {
  if (!iso) return '';
  const days = Math.max(0, Math.floor((Date.now() - new Date(iso + 'T12:00:00').getTime()) / 86400000));
  const yrs = days / 365.25;
  const plural = (n, w) => `${n} ${w}${n === 1 ? '' : 's'} ago`;
  if (days < 1) return 'today';
  if (days < 14) return plural(days, 'day');
  if (days < 60) return plural(Math.round(days / 7), 'week');
  if (yrs < 1) return plural(Math.round(days / 30.44), 'month');
  return `${yrs < 10 ? yrs.toFixed(1).replace(/\.0$/, '') : Math.round(yrs)} years ago`;
}

const STATUS_STYLE = {
  paid: { bg: '#e6f4ea', fg: '#1e7e34' },
  reship: { bg: '#fdeee8', fg: ORANGE },
  canceled: { bg: '#eef1f4', fg: '#5b6b78' },
  deleted: { bg: '#eef1f4', fg: '#5b6b78' },
  hold: { bg: '#fff4d6', fg: '#8a6100' },
  'not completed': { bg: '#fff4d6', fg: '#8a6100' },
};

function Tile({ label, value, sub, accent }) {
  return (
    <div style={{ background: '#fff', borderRadius: 8, padding: '14px 16px', borderTop: `4px solid ${accent ? ORANGE : NAVY}`, boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
      <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: '#5b6b78' }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, color: NAVY, marginTop: 4 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: '#5b6b78', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function Section({ title, children, right }) {
  return (
    <div style={{ background: '#fff', borderRadius: 8, padding: 20, marginBottom: 16, boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12, gap: 12 }}>
        <h2 style={{ fontSize: 17, color: NAVY, margin: 0 }}>{title}</h2>
        {right}
      </div>
      {children}
    </div>
  );
}

function YearChart({ byYear }) {
  if (!byYear.length) return <div style={{ fontSize: 13, color: '#5b6b78' }}>No paid orders.</div>;
  const max = Math.max(...byYear.map((y) => y.spend), 1);
  const H = 180;
  const slot = 64;
  const W = Math.max(byYear.length * slot, 300);
  return (
    <div style={{ overflowX: 'auto' }}>
      <svg width={W} height={H + 48} role="img" aria-label="Orders and spend by year">
        <line x1="0" x2={W} y1={H + 18} y2={H + 18} stroke="#d5dce2" />
        {byYear.map((y, i) => {
          const h = y.spend ? Math.max(3, (y.spend / max) * (H - 24)) : 0;
          const x = i * slot + 10;
          return (
            <g key={y.year}>
              {h > 0 && <rect x={x} y={H + 18 - h} width={slot - 20} height={h} rx="2" fill={NAVY} />}
              <text x={x + (slot - 20) / 2} y={H + 12 - h} textAnchor="middle" fontSize="11" fill="#1d2b36">
                {y.orders ? `${y.orders} · ${money0(y.spend)}` : '0'}
              </text>
              <text x={x + (slot - 20) / 2} y={H + 36} textAnchor="middle" fontSize="12" fill="#5b6b78">{y.year}</text>
            </g>
          );
        })}
      </svg>
      <div style={{ fontSize: 12, color: '#5b6b78', marginTop: 4 }}>Labels show paid orders · spend.</div>
    </div>
  );
}

const TH = { background: NAVY, color: '#fff', textAlign: 'left', padding: '7px 8px', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' };
const TD = { padding: '7px 8px', borderBottom: '1px solid #e3e8ec', fontSize: 13, verticalAlign: 'top' };

export default function CustomerAnalysis({ navigateTo }) {
  const [q, setQ] = useState(() => new URLSearchParams(window.location.search).get('q') || '');
  const [input, setInput] = useState(q);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(async (query) => {
    if (!query) return;
    setLoading(true);
    setError(null);
    try {
      setData(await customerAnalysis(query));
      setShowAll(false);
    } catch (e) {
      setData(null);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(q); }, [q, load]);

  const submit = (e) => {
    e.preventDefault();
    const v = input.trim();
    if (!v) return;
    const url = new URL(window.location.href);
    url.searchParams.set('page', 'customer-analysis');
    url.searchParams.set('q', v);
    window.history.replaceState(null, '', url.toString());
    setQ(v);
  };

  const openOrder = (o) => {
    const id = String(o.bcOrderId || o.orderid);
    if (navigateTo) navigateTo('order-details', id);
  };

  const s = data && data.summary;
  const c = data && data.customer;
  const vip = data && data.vip;
  const p = data && data.problems;
  const orders = data ? data.orders : [];
  const shownOrders = showAll ? orders : orders.slice(0, 50);
  const activeMonths = data ? data.byMonth.filter((m) => m.orders > 0).sort((a, b) => b.orders - a.orders).slice(0, 3) : [];
  const problemCount = p ? p.flaggedOrders.length + p.upsClaims.length + p.chargebacks.length + p.vipChargebacks.length
    + p.reviews.filter((r) => Number(r.rating) <= 3).length : 0;

  return (
    <div style={{ background: '#f4f6f8', minHeight: '100%' }}>
      <form onSubmit={submit} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }} className="no-print">
        <h1 style={{ fontSize: 20, color: NAVY, margin: '0 16px 0 0' }}>Customer Analysis</h1>
        <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Email, customer # or order #"
          className="form-input" style={{ fontSize: 13, minWidth: 280, flex: '0 1 360px' }} />
        <button type="submit" className="btn btn-primary" style={{ fontSize: 12 }}>Run report</button>
        {data && <button type="button" className="btn btn-ghost" style={{ fontSize: 12 }} onClick={() => window.print()}>Print</button>}
      </form>

      {!q && !loading && (
        <div style={{ background: '#fff', borderRadius: 8, padding: 24, fontSize: 14, color: '#5b6b78' }}>
          Enter a customer's email, customer number or an order number to see their whole history: spend by year,
          what they buy, problems on file and every order. You can also open it from the "Customer analysis report"
          link on Order Details and Customer Search.
        </div>
      )}
      {loading && <div style={{ padding: 24, color: '#5b6b78' }}>Pulling the customer's history...</div>}
      {error && <div style={{ background: '#fdeee8', color: '#a33a14', padding: 14, borderRadius: 8 }}>{error}</div>}

      {data && !loading && (
        <>
          <div style={{ background: NAVY, color: '#fff', padding: '20px 24px', borderRadius: 8, marginBottom: 16 }}>
            <div style={{ fontSize: 24, fontWeight: 700 }}>Customer analysis: {c.name}</div>
            <div style={{ fontSize: 14, opacity: 0.85, marginTop: 6 }}>
              {[c.company, c.email, c.phone, c.location,
                c.customerIds.length ? `Customer #${c.customerIds.join(', #')}` : null,
                vip ? (vip.active ? 'VIP member' : 'Former VIP') : 'Never VIP',
                `Pulled ${fmtDate(new Date().toLocaleDateString('en-CA'))}`].filter(Boolean).join(' · ')}
            </div>
            <ListStatus theme="dark" contactIds={c.customerIds.map(String)} email={c.email} style={{ marginTop: 10 }} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12, marginBottom: 16 }}>
            <Tile label="Customer since" value={fmtMonthYear(s.firstOrder)} sub={yearsSince(s.firstOrder)} />
            <Tile label="Paid orders" value={s.paidOrders.toLocaleString()}
              sub={[s.canceledOrders ? `+${s.canceledOrders} canceled` : null, s.reships ? `${s.reships} reships` : null].filter(Boolean).join(' · ') || 'no cancels or reships'} />
            <Tile label="Lifetime spend" value={money0(s.lifetimeSpend)} sub="all paid orders" />
            <Tile label="Avg order" value={money0(s.avgOrder)}
              sub={s.biggestOrder ? `biggest ${money0(s.biggestOrder.amount)}, ${fmtMonthYear(s.biggestOrder.date)}` : null} />
            <Tile label="VIP" accent
              value={vip ? `${vip.renewals} renewal${vip.renewals === 1 ? '' : 's'}` : 'Never'}
              sub={vip ? `Joined ${fmtMonthYear(vip.joined)} · ${vip.active ? `active to ${fmtDate(vip.expires)}${vip.autoRenew ? '' : ', not renewing'}` : `lapsed ${fmtDate(vip.expires)}`}` : 'not a member'} />
            <Tile label="Last order" accent value={fmtMonthYear(s.lastOrder)} sub={yearsSince(s.lastOrder)} />
          </div>

          <Section title="Orders and spend by year">
            <YearChart byYear={data.byYear} />
          </Section>

          <Section title="What they buy">
            {data.topItems.length ? (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><th style={TH}>Item</th><th style={{ ...TH, textAlign: 'right' }}>Orders</th><th style={{ ...TH, textAlign: 'right' }}>Qty</th><th style={{ ...TH, textAlign: 'right' }}>Spend</th></tr></thead>
                <tbody>
                  {data.topItems.map((i) => (
                    <tr key={i.name}>
                      <td style={TD}>{i.name}</td>
                      <td style={{ ...TD, textAlign: 'right' }}>{i.orders}</td>
                      <td style={{ ...TD, textAlign: 'right' }}>{i.qty}</td>
                      <td style={{ ...TD, textAlign: 'right' }}>{money(i.spend)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <div style={{ fontSize: 13, color: '#5b6b78' }}>No paid items.</div>}
            {activeMonths.length > 0 && (
              <div style={{ fontSize: 13, color: '#1d2b36', marginTop: 10 }}>
                Busiest months: {activeMonths.map((m) => `${MONTHS[m.month - 1]} (${m.orders})`).join(', ')}.
              </div>
            )}
          </Section>

          <Section title="Problems on file" right={<span style={{ fontSize: 12, color: problemCount ? ORANGE : '#1e7e34', fontWeight: 700 }}>{problemCount ? `${problemCount} item${problemCount === 1 ? '' : 's'}` : 'None on file'}</span>}>
            {!problemCount && !p.feedback.length && (
              <div style={{ fontSize: 13, color: '#5b6b78' }}>No reships, shipping claims, refunds, chargebacks, low ratings or feedback surveys on file. Check the order notes below and the customer's emails for anything not logged.</div>
            )}
            {p.flaggedOrders.length > 0 && (
              <ul style={{ margin: '0 0 10px', paddingLeft: 20, fontSize: 13, lineHeight: 1.7 }}>
                {p.flaggedOrders.map((f) => (
                  <li key={f.orderid}><b>{fmtDate(f.date)}</b> · order {f.orderid}: {f.flags.join(', ')}</li>
                ))}
              </ul>
            )}
            {p.upsClaims.length > 0 && (
              <div style={{ fontSize: 13, marginBottom: 10 }}><b>UPS claims:</b> {p.upsClaims.map((u) => `${u.order_id} (${u.loss_cause || 'claim'}, ${u.status || 'open'}${u.paid_amount ? `, paid ${money(u.paid_amount)}` : ''})`).join('; ')}</div>
            )}
            {p.chargebacks.length > 0 && (
              <div style={{ fontSize: 13, marginBottom: 10, color: '#a33a14' }}><b>Chargebacks:</b> {p.chargebacks.map((cb) => `${fmtDate(cb.received_date)} ${money(cb.amount)} ${cb.reason || ''} (${cb.status || 'open'})`).join('; ')}</div>
            )}
            {p.vipChargebacks.length > 0 && (
              <div style={{ fontSize: 13, marginBottom: 10, color: '#a33a14' }}><b>VIP chargebacks:</b> {p.vipChargebacks.map((v) => `${fmtDate(v.charge_date)} ${money(v.amount)} ${v.kind || ''}${v.cleared_at ? ' (cleared)' : ''}`).join('; ')}</div>
            )}
            {p.feedback.length > 0 && (
              <div style={{ fontSize: 13, marginBottom: 10 }}>
                <b>Feedback surveys ({p.feedback.length}):</b>{' '}
                {p.feedback.map((f) => `order ${f.orderid} rated ${f.overall}/5${f.comments ? ` - "${f.comments}"` : ''}`).join('; ')}
              </div>
            )}
            {p.reviews.length > 0 && (
              <div style={{ fontSize: 13 }}>
                <b>Reviews ({p.reviews.length}):</b>{' '}
                {p.reviews.map((r) => `${r.rating}/5${r.comment ? ` - "${r.comment}"` : ''}`).join('; ')}
              </div>
            )}
          </Section>

          <Section title={`Full order history (${orders.length})`}
            right={orders.length > 50 && <button className="btn btn-ghost no-print" style={{ fontSize: 12 }} onClick={() => setShowAll((v) => !v)}>{showAll ? 'Show latest 50' : `Show all ${orders.length}`}</button>}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr>
                  <th style={TH}>Date</th><th style={TH}>Order #</th><th style={TH}>Items</th>
                  <th style={{ ...TH, textAlign: 'right' }}>Amount</th><th style={TH}>Status</th><th style={TH}>Notes</th>
                </tr></thead>
                <tbody>
                  {shownOrders.map((o) => {
                    const st = STATUS_STYLE[o.status] || STATUS_STYLE.paid;
                    const flagged = o.flags.length > 0;
                    return (
                      <tr key={o.orderid} style={{ background: flagged ? '#fdeee8' : undefined }}>
                        <td style={{ ...TD, whiteSpace: 'nowrap' }}>{fmtDate(o.date)}</td>
                        <td style={TD}>
                          <button type="button" onClick={() => openOrder(o)} style={{ background: 'none', border: 0, padding: 0, color: ORANGE, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
                            {o.bcOrderId || o.orderid}
                          </button>
                        </td>
                        <td style={TD}>
                          {o.items.filter((i) => i.price > 0 || o.items.length === 1).map((i, k) => (
                            <div key={k}>{i.qty > 1 ? `${i.qty}x ` : ''}{i.name}{i.options ? <span style={{ color: '#5b6b78' }}> ({i.options})</span> : null}</div>
                          ))}
                        </td>
                        <td style={{ ...TD, textAlign: 'right', whiteSpace: 'nowrap' }}>{money(o.amount)}</td>
                        <td style={TD}><span style={{ background: st.bg, color: st.fg, borderRadius: 4, padding: '1px 7px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap', textTransform: 'uppercase' }}>{o.status}</span></td>
                        <td style={{ ...TD, fontSize: 12, maxWidth: 380 }}>
                          {o.flags.length > 0 && <div style={{ color: ORANGE, fontWeight: 700 }}>{o.flags.join(' · ')}</div>}
                          {o.notes.map((n, k) => <div key={k}>{n}</div>)}
                          {o.feedback && <div>Survey: {o.feedback.overall}/5{o.feedback.comments ? ` - "${o.feedback.comments}"` : ''}</div>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Section>
        </>
      )}
    </div>
  );
}
